import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box, keyframes } from '@mui/material';

const EMOJIS = ['✨', '⭐', '💫', '🌟'];

const AmbientParticles = ({ interval = 6000, count = 25, duration = 5000, spread = 200 }) => {
  const [bursts, setBursts] = useState([]);
  const containerRef = useRef(null);
  const nextId = useRef(0);

  const createBurst = useCallback((x, y) => {
    const id = nextId.current++;
    const particles = Array.from({ length: count }, () => {
      const angle = Math.random() * Math.PI * 2;
      const v = 0.3 + Math.random() * 0.7;
      const dx = Math.cos(angle) * v * spread;
      const dy = Math.sin(angle) * v * spread - spread * 0.3;
      return {
        emoji: EMOJIS[Math.floor(Math.random() * EMOJIS.length)],
        dx,
        dy,
        rotation: Math.random() * 360,
        scale: 0.4 + Math.random() * 0.6,
        delay: Math.random() * 400,
      };
    });
    setBursts(prev => [...prev, { id, x, y, particles }]);
    setTimeout(() => setBursts(prev => prev.filter(b => b.id !== id)), duration + 500);
  }, [count, spread, duration]);

  useEffect(() => {
    const fire = () => {
      const x = 20 + Math.random() * 60;
      const y = 20 + Math.random() * 60;
      createBurst(x, y);
    };
    const timer = setInterval(fire, interval);
    return () => clearInterval(timer);
  }, [interval, createBurst]);

  useEffect(() => {
    const el = containerRef.current?.parentElement;
    if (!el) return;
    const handleClick = (e) => {
      const rect = el.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      createBurst(x, y);
    };
    el.addEventListener('click', handleClick);
    return () => el.removeEventListener('click', handleClick);
  }, [createBurst]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 2,
        overflow: 'hidden',
      }}
    >
      {bursts.map(burst => burst.particles.map((p, i) => (
        <Particle key={`${burst.id}-${i}`} burst={burst} p={p} duration={duration} />
      )))}
    </Box>
  );
};

const Particle = ({ burst, p, duration }) => {
  const anim = useMemo(() => keyframes`
    0% { opacity: 1; transform: translate(0, 0) scale(${p.scale}); }
    60% { opacity: 0.7; }
    100% { opacity: 0; transform: translate(${p.dx}px, ${p.dy}px) rotate(${p.rotation}deg) scale(0.1); }
  `, [p.dx, p.dy, p.rotation, p.scale]);

  return (
    <Box
      sx={{
        position: 'absolute',
        left: `${burst.x}%`,
        top: `${burst.y}%`,
        fontSize: 14 * p.scale,
        opacity: 0,
        animation: `${anim} ${duration}ms ease-out ${p.delay}ms forwards`,
      }}
    >
      {p.emoji}
    </Box>
  );
};

export default AmbientParticles;
